import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  fetchTesterAvgTestTime,
  fetchTesterDueThisWeek,
  fetchTesterEvidenceChecklist,
  fetchTesterMyControls,
  fetchTesterMyExceptions,
  fetchTesterMyProgress,
  fetchTesterRerunsRequired,
  fetchTesterTesters,
  type TesterAvgTestTimeResponse,
  type TesterDueThisWeekRow,
  type TesterEvidenceChecklistRow,
  type TesterMyControlsRow,
  type TesterMyExceptionRow,
  type TesterMyProgressRow,
  type TesterOption,
  type TesterRerunsRequiredRow,
} from '../../request/analytics/testerReports.ts';
import { analyticsTesterQueryKeys } from '../../queries/analytics/keys.ts';

/**
 * React Query v5 hooks for the Tester "my work" analytics dashboard
 * (Feature 5.3, reports #30-#36). Each report is its own independent query
 * so a single failing/slow widget doesn't block the rest of the dashboard.
 *
 * Feedback round 2026-08: every hook below now accepts an optional
 * `assigneeUserId` — when omitted, the backend scopes to the caller's own
 * assignments exactly as before; when a manager picks a tester from the
 * dashboard's "Tester" slicer (`useTesterTesters`), it's threaded through to
 * `?assignee_user_id=` on the underlying request.
 */

export function useTesterMyControls(assigneeUserId?: number | null): UseQueryResult<TesterMyControlsRow[], Error> {
  return useQuery({
    queryKey: analyticsTesterQueryKeys.myControls(assigneeUserId),
    queryFn: () => fetchTesterMyControls(assigneeUserId),
  });
}

export function useTesterDueThisWeek(assigneeUserId?: number | null): UseQueryResult<TesterDueThisWeekRow[], Error> {
  return useQuery({
    queryKey: analyticsTesterQueryKeys.dueThisWeek(assigneeUserId),
    queryFn: () => fetchTesterDueThisWeek(assigneeUserId),
  });
}

export function useTesterMyProgress(assigneeUserId?: number | null): UseQueryResult<TesterMyProgressRow[], Error> {
  return useQuery({
    queryKey: analyticsTesterQueryKeys.myProgress(assigneeUserId),
    queryFn: () => fetchTesterMyProgress(assigneeUserId),
  });
}

export function useTesterMyExceptions(assigneeUserId?: number | null): UseQueryResult<TesterMyExceptionRow[], Error> {
  return useQuery({
    queryKey: analyticsTesterQueryKeys.myExceptions(assigneeUserId),
    queryFn: () => fetchTesterMyExceptions(assigneeUserId),
  });
}

/**
 * Report #34 — Evidence Checklist. Parameterized by `runId`; the query stays
 * disabled (and simply doesn't fire) until a run id is supplied, since the
 * backend requires `run_id` as a query param and none of the other Tester
 * reports expose a run id to source one from automatically.
 */
export function useTesterEvidenceChecklist(runId: string | undefined): UseQueryResult<TesterEvidenceChecklistRow[], Error> {
  return useQuery({
    queryKey: analyticsTesterQueryKeys.evidenceChecklist(runId),
    queryFn: () => fetchTesterEvidenceChecklist(runId as string),
    enabled: !!runId,
  });
}

export function useTesterRerunsRequired(assigneeUserId?: number | null): UseQueryResult<TesterRerunsRequiredRow[], Error> {
  return useQuery({
    queryKey: analyticsTesterQueryKeys.rerunsRequired(assigneeUserId),
    queryFn: () => fetchTesterRerunsRequired(assigneeUserId),
  });
}

/** Report (feedback round 2026-08) — Tester Average Test Time, org-wide + per-tester breakdown. */
export function useTesterAvgTestTime(): UseQueryResult<TesterAvgTestTimeResponse, Error> {
  return useQuery({
    queryKey: analyticsTesterQueryKeys.avgTestTime(),
    queryFn: fetchTesterAvgTestTime,
  });
}

/** Distinct testers on current runs — feeds the dashboard's "Tester" slicer. */
export function useTesterTesters(): UseQueryResult<TesterOption[], Error> {
  return useQuery({
    queryKey: analyticsTesterQueryKeys.testers(),
    queryFn: fetchTesterTesters,
  });
}
